import * as React from "react";
import * as RechartsPrimitive from "recharts";

export {
  Area,
  AreaChart,
  Bar,
  BarChart,
  CartesianGrid,
  Cell,
  LabelList,
  Line,
  LineChart,
  Pie,
  PieChart,
  ReferenceArea,
  ReferenceLine,
  ResponsiveContainer,
  XAxis,
  YAxis
} from "recharts";

import { cn } from "@gettactic/helpers";

const THEMES = { light: "", dark: ".dark" } as const;
type IndicatorTypes = "line" | "dot" | "dashed" | "circle";

/**
 * The series of a chart by data key: `label` names it in the tooltip and legend, `color` (or
 * a `theme` pair for light / dark) becomes the CSS variable `--color-<key>`, `icon` and
 * `indicator` replace its mark.
 */
export type ChartConfig = {
  [k in string]: {
    label?: React.ReactNode;
    icon?: React.ComponentType;
    indicator?: IndicatorTypes;
  } & (
    | { color?: string; theme?: never; indicator?: IndicatorTypes }
    | {
        color?: never;
        theme: Record<keyof typeof THEMES, string>;
        indicator?: IndicatorTypes;
      }
  );
};

type ChartContextProps = {
  config: ChartConfig;
};

const ChartContext = React.createContext<ChartContextProps | null>(null);

function useChart() {
  const context = React.useContext(ChartContext);

  if (!context) {
    throw new Error("useChart must be used within a <ChartContainer />");
  }

  return context;
}

/**
 * Wraps a recharts chart (the recharts parts are re-exported here) in a responsive container
 * and publishes `config` colours as `--color-<key>` variables and labels for
 * `ChartTooltipContent` and `ChartLegendContent`. Size it with `className`.
 */
const ChartContainer = React.forwardRef<
  HTMLDivElement,
  React.ComponentProps<"div"> & {
    config: ChartConfig;
    children: React.ComponentProps<
      typeof RechartsPrimitive.ResponsiveContainer
    >["children"];
  }
>(({ id, className, children, config, ...props }, ref) => {
  const uniqueId = React.useId();
  const chartId = `chart-${id || uniqueId.replace(/:/g, "")}`;

  return (
    <ChartContext.Provider value={{ config }}>
      <div
        data-chart={chartId}
        ref={ref}
        className={cn(
          "flex aspect-video justify-center text-xs [&_.recharts-cartesian-axis-tick_text]:fill-muted-foreground [&_.recharts-cartesian-grid_line[stroke='#ccc']]:stroke-border/50 [&_.recharts-curve.recharts-tooltip-cursor]:stroke-border [&_.recharts-dot[stroke='#fff']]:stroke-transparent [&_.recharts-layer]:outline-none [&_.recharts-polar-grid_[stroke='#ccc']]:stroke-border [&_.recharts-radial-bar-background-sector]:fill-muted [&_.recharts-rectangle.recharts-tooltip-cursor]:fill-muted [&_.recharts-reference-line_[stroke='#ccc']]:stroke-border [&_.recharts-sector[stroke='#fff']]:stroke-transparent [&_.recharts-sector]:outline-none [&_.recharts-surface]:outline-none",
          className
        )}
        {...props}
      >
        <ChartStyle id={chartId} config={config} />
        <RechartsPrimitive.ResponsiveContainer>
          {children}
        </RechartsPrimitive.ResponsiveContainer>
      </div>
    </ChartContext.Provider>
  );
});
ChartContainer.displayName = "Chart";

const ChartStyle = ({ id, config }: { id: string; config: ChartConfig }) => {
  const colorConfig = Object.entries(config).filter(
    ([, config]) => config.theme || config.color
  );

  if (!colorConfig.length) {
    return null;
  }

  return (
    <style
      dangerouslySetInnerHTML={{
        __html: Object.entries(THEMES)
          .map(
            ([theme, prefix]) => `
${prefix} [data-chart=${id}] {
${colorConfig
  .map(([key, itemConfig]) => {
    const color =
      itemConfig.theme?.[theme as keyof typeof itemConfig.theme] ||
      itemConfig.color;
    return color ? `  --color-${key}: ${color};` : null;
  })
  .join("\n")}
}
`
          )
          .join("\n")
      }}
    />
  );
};

interface SimpleTooltipProps extends React.HTMLAttributes<HTMLDivElement> {
  children: React.ReactNode;
}

/** The dark tooltip surface of the charts, for tooltips rendered by hand. */
export const SimpleTooltip: React.FC<SimpleTooltipProps> = ({
  children,
  className,
  ...props
}) => {
  return (
    <div
      className={cn(
        "grid min-w-[8rem] items-start gap-0.5 rounded-lg bg-hint-bg px-2.5 py-1.5 text-hint-text text-xs shadow-xl",
        className
      )}
      {...props}
    >
      {children}
    </div>
  );
};

const ChartTooltip = RechartsPrimitive.Tooltip;

/**
 * Content of recharts' `Tooltip`: the hovered category (unless `hideLabel`) and the value of
 * each series with its mark (`indicator`, unless `hideIndicator`).
 */
const ChartTooltipContent = React.forwardRef<
  HTMLDivElement,
  React.ComponentProps<typeof RechartsPrimitive.Tooltip> &
    React.ComponentProps<"div"> & {
      hideLabel?: boolean;
      hideIndicator?: boolean;
      indicator?: "line" | "dot" | "dashed" | "circle";
      nameKey?: string;
      labelKey?: string;
    }
>(
  (
    {
      active,
      payload,
      className,
      indicator: globalIndicator = "dot",
      hideLabel = false,
      hideIndicator = false,
      label,
      labelFormatter,
      labelClassName,
      formatter,
      color,
      nameKey,
      labelKey
    },
    ref
  ) => {
    const { config } = useChart();

    const tooltipLabel = React.useMemo(() => {
      if (hideLabel || !payload?.length) {
        return null;
      }

      const [item] = payload;
      const key = `${labelKey || item.dataKey || item.name || "value"}`;
      const itemConfig = getPayloadConfigFromPayload(config, item, key);
      // The label is the hovered category (e.g. the day), named by the config when it has it
      const value =
        !labelKey && typeof label === "string"
          ? config[label]?.label || label
          : itemConfig?.label;
      if (labelFormatter) {
        return (
          <div className={cn("font-medium flex", labelClassName)}>
            {labelFormatter(value, payload)}
          </div>
        );
      }

      if (!value) {
        return null;
      }

      return <div className={cn("font-medium", labelClassName)}>{value}</div>;
    }, [
      label,
      labelFormatter,
      payload,
      hideLabel,
      labelClassName,
      config,
      labelKey
    ]);
    if (!active || !payload?.length) {
      return null;
    }

    const nestLabel = payload.length === 1;

    return (
      <div
        ref={ref}
        className={cn(
          "grid min-w-[8rem] items-start gap-0.5 rounded-lg bg-hint-bg px-2.5 py-1.5 text-hint-text text-xs shadow-xl",
          className
        )}
      >
        {!nestLabel ? tooltipLabel : null}
        <div className="flex gap-1">
          {payload.map((item, index) => {
            const key = `${nameKey || item.name || item.dataKey || "value"}`;
            const itemConfig = getPayloadConfigFromPayload(config, item, key);
            const indicator = itemConfig?.indicator || globalIndicator;
            const indicatorColor = color || item.payload.fill || item.color;

            return (
              <div
                key={item.dataKey}
                className={cn(
                  "flex w-full flex-wrap items-stretch gap-2 [&>svg]:h-2.5 [&>svg]:w-2.5 [&>svg]:text-muted-foreground",
                  indicator === "dot" && "items-center"
                )}
              >
                {formatter && item?.value !== undefined && item.name ? (
                  formatter(item.value, item.name, item, index, item.payload)
                ) : (
                  <>
                    {itemConfig?.icon ? (
                      <itemConfig.icon />
                    ) : (
                      !hideIndicator && (
                        <ChartIndicator
                          indicator={indicator}
                          nestLabel={nestLabel}
                          indicatorColor={indicatorColor}
                        />
                      )
                    )}
                    <div
                      className={cn(
                        "flex flex-1 justify-between leading-none",
                        nestLabel ? "items-end" : "items-center"
                      )}
                    >
                      {item.value !== undefined && (
                        <span className="font-medium text-hint-text">
                          {item.value.toLocaleString()}
                        </span>
                      )}
                    </div>
                  </>
                )}
              </div>
            );
          })}
        </div>
      </div>
    );
  }
);
ChartTooltipContent.displayName = "ChartTooltip";

const ChartLegend = RechartsPrimitive.Legend;

/**
 * Content of recharts' `Legend`: the mark and `config` label of each series, stacked or in a
 * row (`isRow`). `nameKey` picks the field that names each entry, e.g. a pie slice.
 */
const ChartLegendContent = React.forwardRef<
  HTMLDivElement,
  React.ComponentProps<"div"> &
    Pick<RechartsPrimitive.LegendProps, "payload" | "verticalAlign"> & {
      hideIcon?: boolean;
      nameKey?: string;
      isRow?: boolean;
    }
>(
  (
    {
      className,
      isRow,
      hideIcon = false,
      payload,
      verticalAlign = "bottom",
      nameKey
    },
    ref
  ) => {
    const { config } = useChart();

    if (!payload?.length) {
      return null;
    }

    return (
      <div
        ref={ref}
        className={cn(
          "flex",
          isRow
            ? "flex-row items-center justify-center gap-4"
            : "flex-col justify-start",
          verticalAlign === "top" ? "pb-3" : "pt-3",
          className
        )}
      >
        {payload.map((item) => {
          return (
            <ChartLegendItem
              key={item.value}
              item={item}
              config={config}
              hideIcon={hideIcon}
              isRow={isRow}
              nameKey={nameKey}
            />
          );
        })}
      </div>
    );
  }
);
ChartLegendContent.displayName = "ChartLegend";

/** One entry of `ChartLegendContent`. */
export const ChartLegendItem = ({
  nameKey,
  item,
  config,
  hideIcon,
  isRow
}: {
  nameKey?: string;
  item: any;
  config: ChartConfig;
  hideIcon?: boolean;
  isRow?: boolean;
}) => {
  const key = `${nameKey || item.dataKey || "value"}`;
  const itemConfig = getPayloadConfigFromPayload(config, item, key);

  return (
    <div
      key={item.value}
      className={cn(
        "flex gap-1.5 [&>svg]:h-3 [&>svg]:w-3 [&>svg]:text-foreground",
        isRow ? "items-center" : "items-start justify-start"
      )}
    >
      {itemConfig?.icon && !hideIcon ? (
        <itemConfig.icon />
      ) : (
        <div
          className="h-2 w-2 shrink-0 rounded-[2px]"
          style={{
            backgroundColor: item.color
          }}
        />
      )}
      <span className="text-content">{itemConfig?.label}</span>
    </div>
  );
};

// Helper to extract item config from a payload.
function getPayloadConfigFromPayload(
  config: ChartConfig,
  payload: unknown,
  key: string
) {
  if (typeof payload !== "object" || payload === null) {
    return undefined;
  }

  const payloadPayload =
    "payload" in payload &&
    typeof payload.payload === "object" &&
    payload.payload !== null
      ? payload.payload
      : undefined;

  let configLabelKey: string = key;

  if (
    key in payload &&
    typeof payload[key as keyof typeof payload] === "string"
  ) {
    configLabelKey = payload[key as keyof typeof payload] as string;
  } else if (
    payloadPayload &&
    key in payloadPayload &&
    typeof payloadPayload[key as keyof typeof payloadPayload] === "string"
  ) {
    configLabelKey = payloadPayload[
      key as keyof typeof payloadPayload
    ] as string;
  }

  return configLabelKey in config
    ? config[configLabelKey]
    : config[key as keyof typeof config];
}

/** The mark of a series: "dot", "circle", "line" or "dashed" in its colour. */
const ChartIndicator = ({
  indicator,
  nestLabel,
  indicatorColor
}: {
  indicator: IndicatorTypes;
  nestLabel: boolean;
  indicatorColor: string;
}) => {
  return (
    <div
      className={cn("shrink-0 rounded-[2px]", {
        "h-2.5 w-2.5 rounded-full bg-[var(--color-bg)]": indicator === "dot",
        "h-2.5 w-2.5 rounded-full border-2 border-[color:var(--color-border)]":
          indicator === "circle",
        "w-1": indicator === "line",
        "w-0 border-[1.5px] border-dashed bg-transparent":
          indicator === "dashed",
        "my-0.5": nestLabel && indicator === "dashed"
      })}
      style={
        {
          "--color-bg": indicatorColor,
          "--color-border": indicatorColor
        } as React.CSSProperties
      }
    />
  );
};

export {
  ChartContainer,
  ChartTooltip,
  ChartTooltipContent,
  ChartLegend,
  ChartLegendContent,
  ChartStyle,
  ChartIndicator
};
